import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { FloatLabelType } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';

import { ErrorHelperService } from '@core/helper/error-helper.service';

import { environment } from '@env/environment';
import { PaymentService } from '@core/api/payment.service';
import { UserService } from '@core/api/user.service';
import { AuthMode } from '@feature/auth/auth.component';
import * as fromApp from '@store';
import { logout, setCurrentUser } from '@store/auth/auth.actions';
import { selectCurrentUser } from '@store/auth/auth.selectors';
import { getAdminNotifications, resetStripeAccountRequiresAction, } from '@store/notification/notification.actions';
import * as fromNotification from '@store/notification/notification.reducer';
import { selectNotificationState } from '@store/notification/notification.selectors';
import { LegalConstants } from '@shared/constants/legal.constants';
import { RegexConstants } from '@shared/constants/regex.constants';
import { states } from '@shared/constants/states.contants';
import { DialogManualIDComponent } from '@shared/dialogs/dialog-manual-id/dialog-manual-id.component';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { OtpDialogComponent } from '@shared/dialogs/otp-dialog/otp-dialog.component';
import { Site, StripeAccount, User } from '@shared/models';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { FormatHelperService } from '@core/helper/format-helper.service';
import { AppConfigService } from '@core/api/app-config.service';
import { UserActivity } from '@shared/models/user-activity.model';
import { SiteService } from '@core/api/site.service';

@Component({
  selector: 'fc-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss'],
  standalone: false
})
export class ProfilePageComponent implements OnInit {
  activeSites: Site[] = null;
  addingDelegate: string;
  authModeEnum = AuthMode;
  city: string;
  company: string;
  configuredStripeAccounts: Array<StripeAccount> = [];
  createDelegateForm: UntypedFormGroup;
  createdSites: Site[] = null;
  currentUser: User;
  delegateEmailErrorMessage = '';
  delegateUser: User;
  editingEmail = false;
  editingProfile = false;
  email: string;
  environment = environment;
  error: string;
  errorMessage: string = null;
  hasActiveSites: boolean = false;
  helpEmail: string = '';
  helpPhone: string = '';
  id: number = null;
  isAdmin = false;
  isReadAdmin = false;
  isSuperAdmin = false;
  notificationState: fromNotification.NotificationState;
  phone: string;
  profileEmailErrorMessage = '';
  profileUser: User;
  resetMessage: string;
  showAddDelegate = false;
  sitesLoaded: boolean = false;
  state: string;
  stateList = states;
  streetAddress: string;
  stripeReturn: number = null;
  stripeDisclaimer = LegalConstants.STRIPE_DISCLAIMER;
  stripeRefresh: string = null;
  title: string;
  unconfiguredStripeAccounts: Array<StripeAccount> = [];
  updateEmailForm: UntypedFormGroup;
  updateProfileForm: UntypedFormGroup;
  userFullName: string;
  userType: string = '';
  zip: string;
  userActivityResults: UserActivity[] = [];

  destroyRef = inject(DestroyRef);

  phoneNumberError: boolean = false;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly route: ActivatedRoute,
    readonly paymentService: PaymentService,
    readonly router: Router,
    readonly userService: UserService,
    readonly siteService: SiteService,
    readonly spinnerService: SpinnerService,
    readonly formBuilder: UntypedFormBuilder,
    readonly formatHelperService: FormatHelperService,
    readonly dialog: MatDialog,
    readonly appConfigService: AppConfigService,
    readonly errorHelperService: ErrorHelperService
  ) {
    this.createForms();
  }

  ngOnInit() {
    this.id = +this.route.snapshot.params.id;

    this.stripeReturn = +this.route.snapshot.queryParamMap.get('stripe_return');
    this.stripeRefresh = this.route.snapshot.queryParamMap.get('stripe_refresh');

    if (this.id) {
      this.userService.getUser(this.id).subscribe(user => {
        this.setProfileUser(user);
      });
    }

    this.store
      .select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (!this.currentUser && user) {
          this.currentUser = user;
        }
        if (!this.id) {
          this.setProfileUser(user);
          this.userType = this.getUserType(this.profileUser);
        }
        if (user) {

          // Check if stripe code was returned and token is valid
          if (this.stripeReturn) {
            // refresh user account
            this.userService.refreshStripeAccount(user.id).subscribe(user => {
              this.setProfileUser(user);
              this.configureStripeAccounts();
              this.stripeReturn = null;
            });
          }

          this.userType = this.getUserType(user);
          this.isAdmin = user.isWriteAdmin;
          this.isSuperAdmin = user.isSuperAdmin;
          this.isReadAdmin = user.isReadOnlyAdmin;
        }
      });

    this.store
      .select(selectNotificationState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notificationState => {
        this.notificationState = notificationState;
      });


    // Remove query params so you don't authenticate your stripe account twice which invalidates the account
    this.router.navigate([], {
      queryParams: {
        code: null,
        state: null,
      },
      queryParamsHandling: 'merge',
    });

    this.helpPhone = this.appConfigService.getValue('fc_help_phone');
    this.helpEmail = this.appConfigService.getValue('fc_help_email');

    if (this.stripeRefresh) {
      // popup to show the user that their stripe account is being refreshed
      this.dialog.open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Stripe Account Setup',
          line1: 'Your Stripe account was not successfully set-up. Please try again by clicking the link in your profile.',
          buttonTextNo: 'Close',
          buttonTextYes: 'Refresh',
        },
        autoFocus: false,
      });
    }

    this.createDelegateForm.controls.delegateEmail.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.delegateEmailErrorMessage = this.errorHelperService.getEmailErrorMessage(
          this.createDelegateForm.controls.delegateEmail
        );
      });

    this.updateEmailForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.profileEmailErrorMessage = this.errorHelperService.getEmailErrorMessage(this.updateEmailForm.controls.email);
    });
  }

  configureStripeAccounts() {

    this.unconfiguredStripeAccounts = this.profileUser.stripeAccounts.filter(sa =>
      sa.stripeAccountCreationDate == null
    );
    this.configuredStripeAccounts = this.profileUser.stripeAccounts.filter(
      sa => sa.stripeAccountCreationDate != null
    );
  }

  setProfileUser(user: User) {
    this.profileUser = user;
    let filterString = '';
    if (!this.currentUser || this.profileUser.id !== this.currentUser.id) {
      filterString = `&filters=user_id(${user.id})\``
    }
    this.userService.getUserActivity(`?count=10&sort=event_time(desc)${filterString}`)
      .subscribe(activityResults => {
        this.userActivityResults = activityResults.results;
      });
    if (user && this.profileUser) {
      this.siteService.getAvailableSites(user.id)
        .subscribe(availableSites => {
          this.sitesLoaded = availableSites != null;
          this.createdSites = availableSites || [];
          this.activeSites = availableSites ? availableSites.filter(a => a.activationDate !== null) : null;
          this.hasActiveSites = this.activeSites && this.activeSites.length > 0;
          this.configureStripeAccounts();
        });
      this.delegateUser = this.profileUser.delegate ? new User(this.profileUser.delegate) : null;
      const userFullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      this.userFullName = !isEmpty(userFullName) ? userFullName : '—';
      this.email = !isEmpty(user.email) ? user.email : '—';
      const streetAddress = `${user.street1 || ''} ${user.street2 || ''}`.trim();
      this.streetAddress = streetAddress.length > 0 ? streetAddress : '—';
      this.city = !isEmpty(user.city) ? user.city : '—';
      this.state = !isEmpty(user.state) ? user.state : '—';
      this.company = !isEmpty(user.company) ? user.company : '—';
      this.title = !isEmpty(user.title) ? user.title : '—';
      this.zip = !isEmpty(user.zip) ? user.zip : '—';
      this.phone = !isEmpty(user.phoneE164) ? user.phoneE164.slice(2) : '—';
    }
  }

  isUpdateProfileFormValid(): boolean {
    return this.updateProfileForm.valid;
  }

  createForms(): void {
    this.createDelegateForm = this.formBuilder.group({
      delegateEmail: [null, [Validators.maxLength(254), Validators.pattern(RegexConstants.EMAIL), Validators.required]],
      delegateFirstName: ['', Validators.required],
      delegateLastName: ['', Validators.required],
      delegateTermsClicked: [false, Validators.requiredTrue],
    });
    this.updateProfileForm = this.formBuilder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      company: null,
      title: null,
      address_id: null,
      street: [null, Validators.required],
      street2: null,
      city: [null, Validators.required],
      state: [null, Validators.required],
      zip: [null, Validators.required],
      phone: [null, [Validators.required]],
      mfaEnabled: [null],
      smsOptIn: [null],
    });
    this.updateEmailForm = this.formBuilder.group({
      email: [null, [Validators.maxLength(254), Validators.pattern(RegexConstants.EMAIL), Validators.required]],
    });
  }

  doStripeConnect(): void {
    if (!this.isAdmin) {
      // do api call to create stripe account uuid and save to user
      this.userService.doStripeConnect(
        this.profileUser.id
      );
    }
  }

  getLoginLink(): void {
    if (!this.isAdmin) {
      this.store.dispatch(resetStripeAccountRequiresAction());
      this.paymentService.getMyLoginUrl();
    }
  }

  toggleAddDelegateControls(show: boolean): void {
    this.showAddDelegate = show;
    if (show) {
      this.createDelegateForm.reset();
    } else {
      this.createDelegateForm.markAsPristine();
    }
  }

  onDelegateSubmit(): void {
    this.createDelegateForm.markAsPristine();
    const newDelegate = {
      delegate_email: this.createDelegateForm.value.delegateEmail.toLowerCase(),
      delegate_first_name: this.createDelegateForm.value.delegateFirstName,
      delegate_last_name: this.createDelegateForm.value.delegateLastName,
    };
    this.userService.createDelegate(newDelegate).subscribe({
      next: (updatedUser) => {
        this.setProfileUser(updatedUser);
        this.showAddDelegate = false;
      },
      error: (error) => {
        this.error = error;
      }
    });
  }

  deleteDelegate(): void {
    const dialogRef = this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Are you sure you want to remove this delegate?',
        line1: 'They can be re-added at a later date.',
        buttonTextNo: 'Cancel',
        buttonTextYes: 'Remove',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.userService.removeDelegate().subscribe({
          next: (updatedUser) => {
            this.setProfileUser(updatedUser);
            this.showAddDelegate = false;
          },
          error: (error) => {
            this.error = error;
          }
        });
      }
    });
  }

  toggleProfileEdit(): void {
    this.editingProfile = !this.editingProfile;
    this.updateProfileForm.controls.city.setValue(this.profileUser.city);
    this.updateProfileForm.controls.firstName.setValue(this.profileUser.firstName);
    this.updateProfileForm.controls.lastName.setValue(this.profileUser.lastName);
    this.updateProfileForm.controls.mfaEnabled.setValue(this.profileUser.mfaEnabled);
    this.updateProfileForm.controls.smsOptIn.setValue(this.profileUser.smsOptIn);
    this.updateProfileForm.controls.company.setValue(this.profileUser.company);
    this.updateProfileForm.controls.title.setValue(this.profileUser.title);
    this.updateProfileForm.controls.phone.setValue(this.profileUser.phoneE164);
    this.updateProfileForm.controls.state.setValue(this.profileUser.state);
    this.updateProfileForm.controls.street2.setValue(this.profileUser.street2);
    this.updateProfileForm.controls.street.setValue(this.profileUser.street1);
    this.updateProfileForm.controls.zip.setValue(this.profileUser.zip);

    if (this.profileUser.mfaEnabled || this.profileUser.smsOptIn) {
      this.updateProfileForm.controls.phone.disable();
    } else {
      this.updateProfileForm.controls.phone.enable();
    }

    // mark form as pristine so the user can navigate if they cancel
    if (!this.editingProfile) {
      this.updateProfileForm.markAsPristine();
    }
  }

  toggleEmailEdit(): void {
    this.editingEmail = !this.editingEmail;
    this.updateEmailForm.controls.email.setValue(this.profileUser.email);

    if (!this.editingEmail) {
      this.updateEmailForm.markAsPristine();
    }
  }

  confirmSMSOptIn(checkboxChange: MatCheckboxChange): void {
    if (checkboxChange.checked) {
      this.dialog
        .open(DialogYesNoComponent, {
          panelClass: 'fc-yes-no-dialog',
          data: {
            title: 'SMS Opt-In Confirmation',
            line1: 'By opting in to SMS notifications, you agree to receive text messages regarding your account and sites. Message and data rates may apply. You can opt out at any time.',
            buttonTextNo: 'Cancel',
            buttonTextYes: 'Opt In',
          },
        })
        .afterClosed()
        .subscribe((result: boolean): void => {
          // Show OTP dialog if user confirms they want to opt in, otherwise uncheck the box and set form value to false
          if (result && !this.updateProfileForm.controls.mfaEnabled.value) {
            this.dialog
              .open(OtpDialogComponent, {
                data: {
                  mode: 'sms-verify',
                  phoneNumber: this.updateProfileForm.controls.phone.value
                }
              })
              .afterClosed()
              .subscribe((result: boolean): void => {
                this.setMfaEnabled(checkboxChange, result, 'smsOptIn');
              });
          } else {
            this.setMfaEnabled(checkboxChange, result, 'smsOptIn');
          }
        });
    }
  }

  setMfaEnabled(checkboxChange: MatCheckboxChange, value: boolean, formControlName: string = 'mfaEnabled'): void {
    checkboxChange.source.checked = value;
    this.updateProfileForm.controls[formControlName].setValue(value);
    this.updateProfileForm.updateValueAndValidity();
    // if its true, disable the phone field
    this.checkPhoneFields();
  }

  checkPhoneFields() {
    // if phone number has changed, disable smsOptIn and mfaEnabled
    if (this.phone !== this.updateProfileForm.controls.phone.value) {
      this.updateProfileForm.controls.smsOptIn.disable();
      this.updateProfileForm.controls.mfaEnabled.disable();
    } else {
      this.updateProfileForm.controls.smsOptIn.enable();
      this.updateProfileForm.controls.mfaEnabled.enable();
    }
    // if either smsOptIn or mfaEnabled are true, disable phone field
    if (this.updateProfileForm.controls.smsOptIn.value || this.updateProfileForm.controls.mfaEnabled.value) {
      this.updateProfileForm.controls.phone.disable();
    } else {
      // if both are false, enable phone field
      this.updateProfileForm.controls.phone.enable();
    }
  }

  confirmMFA(checkboxChange: MatCheckboxChange): void {
    if (checkboxChange.checked) {
      this.dialog
        .open(OtpDialogComponent, { data: { mode: 'verify-phone' } })
        .afterClosed()
        .subscribe((result: boolean): void => {
          this.setMfaEnabled(checkboxChange, result);
        });
    } else if (this.isAdmin) {
      this.setMfaEnabled(checkboxChange, false);
    } else {
      this.dialog
        .open(DialogYesNoComponent, {
          panelClass: 'fc-confirmation-dialog',
          data: {
            title: 'Disable MFA?',
            line1:
              'You are about to disabled MFA. You will receive a confirmation code via SMS, which will complete your removal from MFA. If you no longer have access to the phone number above, please contact support to disable MFA and/or set up a new phone number. You can opt back in at any time.',
            buttonTextNo: 'Never Mind',
            buttonTextYes: 'Confirm',
          },
        })
        .afterClosed()
        .subscribe((result?: boolean): void => {
          if (result) {
            this.dialog
              .open(OtpDialogComponent, { data: { mode: 'verify-phone' } })
              .afterClosed()
              .subscribe((result: boolean): void => {
                if (result) {
                  this.setMfaEnabled(checkboxChange, false);
                } else {
                  this.setMfaEnabled(checkboxChange, true);
                }
              });
          } else {
            this.setMfaEnabled(checkboxChange, true);
          }
        });
    }
  }

  updateProfile(): void {
    const profile = {
      city: this.updateProfileForm.value.city,
      date_of_birth: new Date(this.profileUser.dateOfBirth).getTime() / 1000,
      first_name: this.updateProfileForm.value.firstName,
      company: this.updateProfileForm.value.company,
      title: this.updateProfileForm.value.title,
      id: this.profileUser.id,
      last_name: this.updateProfileForm.value.lastName,
      mfa_enabled: this.updateProfileForm.controls.mfaEnabled.value,
      sms_opt_in: this.updateProfileForm.controls.smsOptIn.value,
      phone_e_164: this.updateProfileForm.controls.phone.value,
      state: this.updateProfileForm.value.state,
      street_1: this.updateProfileForm.value.street,
      street_2: this.updateProfileForm.value.street2,
      zip: this.updateProfileForm.value.zip,
    };
    this.userService.updateUserProfile(profile).subscribe((user: User) => {
      // Checks if current user is changing themselves and only update current user if so
      if (this.currentUser.id === user.id) {
        this.currentUser = user;
        this.store.dispatch(setCurrentUser({ data: user }));
      }
      this.setProfileUser(user);
      this.toggleProfileEdit();
      this.checkPhoneFields();
    });
  }

  updateEmail(): void {
    const email = this.updateEmailForm.value.email;
    if (email !== this.profileUser.email) {
      const dialogRef = this.dialog.open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Change Email',
          line1: 'Changing your email address will also change your login credentials, and will automatically log you out of the system. We will send you an email to verify your new address. Next time you log in, enter your updated email.',
          buttonTextNo: 'Cancel',
          buttonTextYes: 'Change Email',
        },
        autoFocus: false,
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.userService.changeEmailAddress(this.profileUser.id, email).subscribe(() => {
            this.store.dispatch(logout());
          });
        }
      });
    } else {
      this.toggleEmailEdit();
    }
  }

  verifyAccount(): void {
    if (this.profileUser && !this.profileUser.isVerified) {
      this.dialog
        .open(DialogManualIDComponent, {
          panelClass: 'fc-yes-no-dialog',
          data: {
            submitTimes: this.profileUser.identityAttempts,
          },
        })
        .afterClosed()
        .subscribe(result => {
          if (result && result !== 'close') {
            const action = result;
            this.dialog
              .open(DialogYesNoComponent, {
                panelClass: 'fc-yes-no-dialog',
                data: {
                  title: 'Are You Sure?',
                  line1: `You are about to ${action} this user.`,
                  line2: 'Please confirm.',
                  buttonTextNo: 'Cancel',
                  buttonTextYes: 'Confirm',
                },
              })
              .afterClosed()
              .subscribe(result => {
                if (result) {
                  const verifyConfig = {
                    verify: {
                      verified: true,
                      clear: false,
                    },
                    deny: {
                      verified: false,
                      clear: false,
                    },
                    clear: {
                      verified: false,
                      clear: true,
                    },
                  };

                  this.userService
                    .verifyUser(this.profileUser.id, verifyConfig[action].verified, verifyConfig[action].clear)
                    .subscribe((user: User) => {
                      this.setProfileUser(user);
                      this.store.dispatch(getAdminNotifications());
                    });
                }
              });
          }
        });
    }
  }

  sendAdminPasswordReset(): void {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Please Confirm',
          line1: "This will reset this user's password immediately. Are you sure you want to continue?",
          line2: '',
          buttonTextYes: 'Yes, Reset',
          buttonTextNo: 'No, Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.userService.adminResetPassword(this.id).subscribe((result: boolean) => {
            this.resetMessage = result ? 'Reset e-Mail Sent' : 'There was a problem resetting the password';
            setTimeout(() => {
              this.resetMessage = '';
            }, 3000);
          });
        }
      });
  }

  openPlaid() {
    if (this.profileUser?.identityProviderId) {
      window.open(
        `https://dashboard.plaid.com/${environment.env == 'prod' ? '' : 'sandbox/'}identity_verification/sessions/${this.profileUser.identityProviderId}`,
        '_blank'
      );
    }
  }

  getUserType(user: User) {
    if (user?.isWriteAdmin) {
      return 'Admin';
    } else {
      return 'Landowner';
    }
  }

  get isFormDirty(): boolean {
    return this.updateProfileForm.dirty || this.updateEmailForm.dirty || this.createDelegateForm.dirty;
  }

  deleteUser() {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Delete User',
          line1: 'This will permanently delete this user from LandYield.',
          line2: 'Do you wish to continue?',
          buttonTextYes: 'Confirm',
          buttonTextNo: 'Cancel',
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(confirm => {
        if (confirm) {
          this.dialog
            .open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'Final Confirmation',
                line1: 'This is not reversible.',
                line2: 'All user data and cancelled sites will be deleted!',
                buttonTextYes: 'Confirm',
                buttonTextNo: 'Cancel',
              },
              autoFocus: false,
            })
            .afterClosed()
            .subscribe(againConfirm => {
              if (againConfirm) {
                this.userService.deleteUser(this.profileUser.id).subscribe(result => {
                  if (result) {
                    this.router.navigate(['admin', 'permissions']);
                  }
                });
              }
            });
        }
      });
  }

  navigateSendEmail(): void {
    this.router.navigate(['admin', 'manage', 'email', 'send'], {
      state: { users: [this.profileUser] },
    });
  }

  getFloatLabelValue(): FloatLabelType {
    return this.updateProfileForm.controls.phone.value || 'auto';
  }

  formatPhone($event: any) {
    this.phoneNumberError = this.formatHelperService.formatPhone($event, this.updateProfileForm.controls.phone)
    this.checkPhoneFields();
  }
}
